import { createAsyncThunk } from '@reduxjs/toolkit';
import adserver from 'services/adserver';
import { withErrorHandling } from 'store/wrappers';
import { REDUCER_KEY } from './counterOfferProposals.consts';
import { ProposalParams } from '../proposals.types';
import { normalizeDateFromApi } from 'helpers/datetimeHelpers';
import { getCounterOfferFlights } from 'store/flights/counterOffer/counterOfferFlights.actions';

function normalizeApiReponse(data: ProposalParams) {
  if (!data.productSnapshot.timedSegments) {
    return data;
  }
  const segments = data.productSnapshot.timedSegments.map(segment => ({
    ...segment,
    startDate: normalizeDateFromApi(segment.startDate),
    endDate: segment.endDate ? normalizeDateFromApi(segment.endDate) : null,
  }));
  return {
    ...data,
    productSnapshot: { ...data.productSnapshot, timedSegments: segments },
  };
}

export const getCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/getCounterOffer',
  withErrorHandling(async ({ proposalId }: { proposalId: number }) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer`,
      method: 'GET',
      headers: {},
    });
    return normalizeApiReponse({ ...response.data, id: proposalId });
  }),
);

const fetchByIdHandler = async ({ proposalId }: { proposalId: number }) => {
  const proposal = await adserver({
    url: `/proposal/${proposalId}/counteroffer`,
    headers: {},
  });

  const surcharges = await adserver({
    url: `/proposal/${proposalId}/surcharges`,
    headers: {},
  });
  return { ...proposal.data, surcharges: surcharges?.data };
};

export const setCurrentCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/current',
  withErrorHandling(async (proposalId: number, thunkAPI: any) => {
    if (!proposalId) return;
    const counterOffer = await fetchByIdHandler({ proposalId });

    thunkAPI.dispatch(getCounterOfferFlights({ proposalId }));
    return normalizeApiReponse(counterOffer);
  }),
);

/**
 * Counter Offer Actions
 */
export const createCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/createCounterOffer',
  withErrorHandling(
    async ({
      proposalId,
      productId,
    }: {
      proposalId: number;
      productId: number;
    }) => {
      if (!proposalId || !productId)
        throw new Error('no proposalId or productId');
      const response = await adserver({
        url: `/proposal/${proposalId}/counteroffer/`,
        method: 'POST',
        headers: {},
        data: { product: productId },
      });
      return normalizeApiReponse({ ...response.data });
    },
  ),
);

export const removeCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/removeCounterOffer',
  withErrorHandling(async (proposalId: number) => {
    await adserver({
      url: `/proposal/${proposalId}/counteroffer`,
      method: 'DELETE',
      headers: {},
    });
    return proposalId;
  }),
);

export const updateCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/updateCounterOffer',
  withErrorHandling(
    async ({
      proposalId,
      data,
    }: {
      proposalId: number;
      data: ProposalParams;
    }) => {
      await adserver({
        url: `/proposal/${proposalId}/counteroffer`,
        method: 'PATCH',
        headers: {},
        data,
      });
      return data.id;
    },
  ),
);

export const submitCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/submitCounterOffer',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer/submit`,
      method: 'POST',
      headers: {},
    });

    return normalizeApiReponse(response.data);
  }),
);

export const fetchCounterOfferProposalFormRate = createAsyncThunk(
  REDUCER_KEY + '/fetchCounterOfferProposalFormRate',
  withErrorHandling(
    async ({
      proposalId,
      formData,
    }: {
      proposalId: number;
      formData: ProposalParams;
    }) => {
      const response = await adserver({
        url: `/proposal/${proposalId}/rate/counteroffer`,
        method: 'POST',
        data: formData,
        timeout: 10000,
        headers: {},
      });
      return response;
    },
  ),
);
